import React, { useMemo, useState } from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { 
  GripVertical, Plus, Trash2, ChevronDown, ChevronUp, 
  Type, Hash, CheckSquare, List, Star, AlertCircle, Lightbulb, Trophy
} from 'lucide-react';
import { Question } from '../types/api';

interface FormQuestionBuilderProps {
  value: Question[];
  onChange: (questions: Question[]) => void;
}

const ANSWER_TYPES = [
  { value: 'text', label: 'Text', icon: Type, color: 'blue' },
  { value: 'number', label: 'Number', icon: Hash, color: 'green' },
  { value: 'mcq', label: 'Multiple Choice', icon: List, color: 'purple' },
  { value: 'checkbox', label: 'Checkbox', icon: CheckSquare, color: 'orange' },
];

const FormQuestionBuilder: React.FC<FormQuestionBuilderProps> = ({ value, onChange }) => {
  const [localQuestions, setLocalQuestions] = useState<Question[]>(value || []);
  const [expandedQuestions, setExpandedQuestions] = useState<Set<number>>(new Set([0]));

  useMemo(() => {
    setLocalQuestions(value || []);
  }, [value]);

  const updateQuestions = (next: Question[]) => {
    setLocalQuestions(next);
    onChange(next);
  };

  const toggleExpanded = (index: number) => {
    setExpandedQuestions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const expandAll = () => {
    setExpandedQuestions(new Set(localQuestions.map((_, i) => i)));
  };

  const collapseAll = () => {
    setExpandedQuestions(new Set());
  };

  const addQuestion = () => {
    const newQuestion: Question = {
      question: '',
      isRequired: false,
      answer_type: 'mcq',
      score: undefined,
      answer_text: '',
      answers_mcq: { options: ['Yes', 'No'], interpretations: ['', ''], score: [0, 0], answer: '' },
      questionText: '',
    };
    const newIndex = localQuestions.length;
    updateQuestions([...(localQuestions || []), newQuestion]);
    setExpandedQuestions(prev => new Set(Array.from(prev).concat(newIndex)));
  };

  const removeQuestion = (index: number) => {
    const next = [...localQuestions];
    next.splice(index, 1);
    updateQuestions(next);
    setExpandedQuestions(prev => {
      const newSet = new Set<number>();
      prev.forEach(i => {
        if (i < index) newSet.add(i);
        else if (i > index) newSet.add(i - 1);
      });
      return newSet;
    });
  };

  const updateField = (index: number, key: keyof Question, val: any) => {
    const next = localQuestions.map((q, i) => (i === index ? { ...q, [key]: val } : q));
    if (key === 'answer_type' && !(val === 'mcq' || val === 'checkbox')) {
      next[index] = { ...next[index] };
      delete (next[index] as any).answers_mcq;
    }
    if (key === 'answer_type' && (val === 'mcq' || val === 'checkbox')) {
      next[index] = {
        ...next[index],
        answers_mcq: next[index].answers_mcq || { options: ['Yes', 'No'], interpretations: ['', ''], score: [0, 0], answer: '' },
      };
    }
    updateQuestions(next);
  };

  const updateMcqOption = (qIdx: number, optIdx: number, val: string) => {
    const next = [...localQuestions];
    const mcq = next[qIdx].answers_mcq || { options: [], interpretations: [], answer: '' };
    const options = [...(mcq.options || [])];
    options[optIdx] = val;
    next[qIdx] = { ...next[qIdx], answers_mcq: { ...mcq, options } };
    updateQuestions(next);
  };

  const updateMcqInterpretation = (qIdx: number, optIdx: number, val: string) => {
    const next = [...localQuestions];
    const mcq = next[qIdx].answers_mcq || { options: [], interpretations: [], answer: '' };
    const interpretations = [...(mcq.interpretations || [])];
    // Ensure interpretations array has enough slots
    while (interpretations.length <= optIdx) {
      interpretations.push('');
    }
    interpretations[optIdx] = val;
    next[qIdx] = { ...next[qIdx], answers_mcq: { ...mcq, interpretations } };
    updateQuestions(next);
  };

  const updateMcqScore = (qIdx: number, optIdx: number, val: number | string) => {
    const next = [...localQuestions];
    const mcq = next[qIdx].answers_mcq || { options: [], interpretations: [], score: [], answer: '' };
    const scores = [...(mcq.score || [])];
    while (scores.length <= optIdx) {
      scores.push(0);
    }
    scores[optIdx] = val as any;
    next[qIdx] = { ...next[qIdx], answers_mcq: { ...mcq, score: scores } };
    updateQuestions(next);
  };

  const updateQuestionAndLabel = (index: number, val: string) => {
    const next = localQuestions.map((q, i) => (i === index ? { ...q, question: val, questionText: val } : q));
    updateQuestions(next);
  };

  const addMcqOption = (qIdx: number) => {
    const next = [...localQuestions];
    const mcq = next[qIdx].answers_mcq || { options: [], interpretations: [], score: [], answer: '' };
    const newOptions = [...(mcq.options || []), ''];
    const newInterpretations = [...(mcq.interpretations || []), ''];
    const newScores = [...(mcq.score || []), 0];
    next[qIdx] = { ...next[qIdx], answers_mcq: { ...mcq, options: newOptions, interpretations: newInterpretations, score: newScores } };
    updateQuestions(next);
  };

  const removeMcqOption = (qIdx: number, optIdx: number) => {
    const next = [...localQuestions];
    const mcq = next[qIdx].answers_mcq || { options: [], interpretations: [], score: [], answer: '' };
    const options = [...(mcq.options || [])];
    const interpretations = [...(mcq.interpretations || [])];
    const scores = [...(mcq.score || [])];
    options.splice(optIdx, 1);
    if (interpretations.length > optIdx) {
      interpretations.splice(optIdx, 1);
    }
    if (scores.length > optIdx) {
      scores.splice(optIdx, 1);
    }
    next[qIdx] = { ...next[qIdx], answers_mcq: { ...mcq, options, interpretations, score: scores } };
    updateQuestions(next);
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    
    const sourceIndex = result.source.index;
    const destIndex = result.destination.index;
    
    if (sourceIndex === destIndex) return;

    const reorderedQuestions = [...localQuestions];
    const [removed] = reorderedQuestions.splice(sourceIndex, 1);
    reorderedQuestions.splice(destIndex, 0, removed);
    
    updateQuestions(reorderedQuestions);

    // Update expanded indices
    setExpandedQuestions(prev => {
      const newSet = new Set<number>();
      prev.forEach(i => {
        if (i === sourceIndex) {
          newSet.add(destIndex);
        } else if (sourceIndex < destIndex) {
          if (i > sourceIndex && i <= destIndex) newSet.add(i - 1);
          else newSet.add(i);
        } else {
          if (i >= destIndex && i < sourceIndex) newSet.add(i + 1);
          else newSet.add(i);
        }
      });
      return newSet;
    });
  };

  const getAnswerTypeIcon = (type: string) => {
    const found = ANSWER_TYPES.find(t => t.value === type);
    if (!found) return Type;
    return found.icon;
  };

  const getAnswerTypeColor = (type: string) => {
    const found = ANSWER_TYPES.find(t => t.value === type);
    return found?.color || 'gray';
  };

  return (
    <div className="question-builder">
      {/* Header */}
      <div className="question-builder-header">
        <div className="question-count">
          {localQuestions.length} {localQuestions.length === 1 ? 'Question' : 'Questions'}
        </div>
        {localQuestions.length > 1 && (
          <div className="question-controls">
            <span className="drag-hint">Drag to reorder</span>
            <button type="button" onClick={expandAll} className="control-btn">
              <ChevronDown size={14} /> Expand All
            </button>
            <button type="button" onClick={collapseAll} className="control-btn">
              <ChevronUp size={14} /> Collapse All
            </button>
          </div>
        )}
      </div>

      {/* Empty State */}
      {(!localQuestions || localQuestions.length === 0) && (
        <div className="question-empty-state">
          <div className="empty-icon">
            <AlertCircle size={48} />
          </div>
          <h4>No questions added yet</h4>
          <p>Start building your form by adding the first question</p>
          <button type="button" className="btn btn-primary" onClick={addQuestion}>
            <Plus size={16} />
            Add First Question
          </button>
        </div>
      )}

      {/* Questions List */}
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="questions">
          {(provided, snapshot) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
              className={`questions-drop-zone ${snapshot.isDraggingOver ? 'dragging-over' : ''}`}
            >
              {localQuestions?.map((q, idx) => {
                const isExpanded = expandedQuestions.has(idx);
                const TypeIcon = getAnswerTypeIcon(q.answer_type);
                const typeColor = getAnswerTypeColor(q.answer_type);
                
                return (
                  <Draggable key={`question-${idx}`} draggableId={`question-${idx}`} index={idx}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        className={`question-card ${snapshot.isDragging ? 'dragging' : ''} ${isExpanded ? 'expanded' : ''}`}
                        style={provided.draggableProps.style}
                      >
                        {/* Question Header */}
                        <div className="question-card-header" onClick={() => toggleExpanded(idx)}>
                          <div
                            {...provided.dragHandleProps}
                            className="drag-handle"
                            title="Drag to reorder"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <GripVertical size={18} />
                          </div>
                          
                          <div className="question-number-badge">
                            {idx + 1}
                          </div>
                          
                          <div className="question-preview">
                            <span className="question-text-preview">
                              {q.question || q.questionText || 'Untitled Question'}
                            </span>
                            <div className="question-badges">
                              <span className={`type-badge type-${typeColor}`}>
                                <TypeIcon size={12} />
                                {ANSWER_TYPES.find(t => t.value === q.answer_type)?.label || 'Text'}
                              </span>
                              {q.isRequired && (
                                <span className="required-badge">
                                  <Star size={10} /> Required
                                </span>
                              )}
                              {q.score !== undefined && q.score !== null && (
                                <span className="score-badge">
                                  {q.score} pts
                                </span>
                              )}
                            </div>
                          </div>
                          
                          <button type="button" className="expand-toggle">
                            {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                          </button>
                        </div>

                        {/* Question Body (Expandable) */}
                        <div className={`question-card-body ${isExpanded ? 'show' : ''}`}>
                          {/* Question Text */}
                          <div className="question-field">
                            <label className="field-label">
                              Question Text <span className="required-star">*</span>
                            </label>
                            <input
                              type="text"
                              className="field-input"
                              value={q.questionText || q.question || ''}
                              onChange={(e) => updateQuestionAndLabel(idx, e.target.value)}
                              placeholder="Enter your question here..."
                            />
                          </div>

                          {/* Settings Row */}
                          <div className="question-settings-row">
                            <div className="setting-group">
                              <label className="field-label">Answer Type</label>
                              <div className="answer-type-selector">
                                {ANSWER_TYPES.map(type => (
                                  <button
                                    key={type.value}
                                    type="button"
                                    className={`type-btn ${q.answer_type === type.value ? 'active' : ''} type-${type.color}`}
                                    onClick={() => updateField(idx, 'answer_type', type.value)}
                                  >
                                    <type.icon size={16} />
                                    {type.label}
                                  </button>
                                ))}
                              </div>
                            </div>
                          </div>

                          <div className="question-settings-row">
                            <div className="setting-group half">
                              <label className="field-label">Required</label>
                              <div className="toggle-switch-container">
                                <button
                                  type="button"
                                  className={`toggle-option ${!q.isRequired ? 'active' : ''}`}
                                  onClick={() => updateField(idx, 'isRequired', false)}
                                >
                                  Optional
                                </button>
                                <button
                                  type="button"
                                  className={`toggle-option required ${q.isRequired ? 'active' : ''}`}
                                  onClick={() => updateField(idx, 'isRequired', true)}
                                >
                                  <Star size={12} /> Required
                                </button>
                              </div>
                            </div>
                            
                            <div className="setting-group half">
                              <label className="field-label">Score (optional)</label>
                              <input
                                type="number"
                                className="field-input score-input"
                                value={q.score ?? ''}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  updateField(idx, 'score', v === '' ? undefined : Number(v));
                                }}
                                placeholder="e.g. 10"
                              />
                            </div>
                          </div>

                          {/* MCQ Options with Interpretations */}
                          {(q.answer_type === 'mcq' || q.answer_type === 'checkbox') && (
                            <div className="mcq-options-section">
                              <label className="field-label">
                                Options & Interpretations
                                <span className="option-count">({q.answers_mcq?.options?.length || 0})</span>
                              </label>
                              <div className="mcq-options-header">
                                <span className="header-option">Option</span>
                                <span className="header-interpretation">
                                  <Lightbulb size={14} />
                                  Interpretation
                                </span>
                                <span className="header-score">
                                  <Trophy size={14} />
                                  Score
                                </span>
                              </div>
                              <div className="mcq-options-list">
                                {(q.answers_mcq?.options || []).map((opt, oIdx) => (
                                  <div key={oIdx} className="mcq-option-row with-interpretation">
                                    <div className="option-indicator">
                                      {q.answer_type === 'checkbox' ? (
                                        <CheckSquare size={16} />
                                      ) : (
                                        <div className="radio-circle" />
                                      )}
                                    </div>
                                    <input
                                      type="text"
                                      className="option-input"
                                      value={opt}
                                      onChange={(e) => updateMcqOption(idx, oIdx, e.target.value)}
                                      placeholder={`Option ${oIdx + 1}`}
                                    />
                                    <div className="interpretation-separator">→</div>
                                    <input
                                      type="text"
                                      className="interpretation-input"
                                      value={q.answers_mcq?.interpretations?.[oIdx] || ''}
                                      onChange={(e) => updateMcqInterpretation(idx, oIdx, e.target.value)}
                                      placeholder={`Interpretation for option ${oIdx + 1}`}
                                    />
                                    <input
                                      type="text"
                                      inputMode="numeric"
                                      className="score-option-input"
                                      value={q.answers_mcq?.score?.[oIdx] ?? ''}
                                      onChange={(e) => {
                                        const raw = e.target.value;
                                        if (raw === '' || raw === '-' || /^-?\d*\.?\d*$/.test(raw)) {
                                          updateMcqScore(idx, oIdx, raw);
                                        }
                                      }}
                                      onBlur={(e) => {
                                        const num = parseFloat(e.target.value);
                                        updateMcqScore(idx, oIdx, isNaN(num) ? 0 : num);
                                      }}
                                      placeholder="0"
                                      title="Score for this option"
                                    />
                                    <button 
                                      type="button" 
                                      className="remove-option-btn"
                                      onClick={() => removeMcqOption(idx, oIdx)}
                                      title="Remove option"
                                    >
                                      <Trash2 size={14} />
                                    </button>
                                  </div>
                                ))}
                              </div>
                              <button 
                                type="button" 
                                className="add-option-btn"
                                onClick={() => addMcqOption(idx)}
                              >
                                <Plus size={14} />
                                Add Option
                              </button>
                            </div>
                          )}

                          {/* Delete Question */}
                          <div className="question-actions">
                            <button 
                              type="button" 
                              className="delete-question-btn"
                              onClick={() => removeQuestion(idx)}
                            >
                              <Trash2 size={14} />
                              Delete Question
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </Draggable>
                );
              })}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      {/* Add Question Button */}
      {(localQuestions && localQuestions.length > 0) && (
        <button type="button" className="add-question-btn" onClick={addQuestion}>
          <Plus size={18} />
          Add New Question
        </button>
      )}
    </div>
  );
};

export default FormQuestionBuilder;
